import { agentSessionDocumentsOptions, useUploadSessionDocument } from "@app/queries/agent";
import type { ExperimentId } from "@globals";
import { notifications } from "@lib/notification/notification";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";

// Mirror of the per-file size and per-session count (Django settings AGENT_DOCUMENT_MAX_*) and the
// extension allow-list (atinary_util.document_formats), enforced at upload time by the endpoint.
// Surfaced client-side so the user gets immediate feedback instead of a failed upload round-trip.
// Keep in sync — the backend stays authoritative.
export const MAX_FILE_BYTES = 4_500_000;
export const MAX_DOCS_PER_SESSION = 5;
export const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.txt,.md";

export type FileAdmission = {
  accepted: File[];
  oversized: File[];
  droppedBySlots: number;
};

/**
 * Pure client-side admission of picked files against the session document caps, mirroring the
 * backend's per-file size and per-count limits so the user gets immediate feedback. The backend
 * stays authoritative (it counts the whole session cumulatively); this only sees the `existingCount`
 * the caller passes. Drop order: per-file size, then count slots. Callers surface their own
 * warnings from the returned counts.
 */
export function admitFiles(incoming: File[], { existingCount }: { existingCount: number }): FileAdmission {
  const oversized = incoming.filter(f => f.size > MAX_FILE_BYTES);
  const sized = incoming.filter(f => f.size <= MAX_FILE_BYTES);

  const slotsLeft = MAX_DOCS_PER_SESSION - existingCount;
  const accepted = sized.slice(0, Math.max(0, slotsLeft));
  const droppedBySlots = sized.length - accepted.length;

  return { accepted, oversized, droppedBySlots };
}

/**
 * Surface the standard cap/size warnings for an {@link admitFiles} result. Both attach paths
 * (immediate upload and the deferred ChatBox mode) call this so the warning copy lives in one place.
 */
export function notifyAdmissionWarnings({ oversized, droppedBySlots }: FileAdmission): void {
  if (oversized.length > 0) {
    notifications.warning(
      "File too large",
      `${oversized.map(f => f.name).join(", ")} exceeds the ${(MAX_FILE_BYTES / 1_000_000).toFixed(1)} MB per-file limit.`
    );
  }
  if (droppedBySlots > 0) {
    notifications.warning(
      "Document limit reached",
      `Only ${MAX_DOCS_PER_SESSION} documents can be attached at a time. ${droppedBySlots} file(s) were not added.`
    );
  }
}

export type AttachmentStatus = "uploading" | "uploaded" | "error";

// One row in the attachment-chip list. `localId` is a client-only key that is stable across
// the uploading → uploaded transition. Uploaded documents can't be removed individually —
// only clearing the whole chat removes them — so there is no server handle to keep here.
export type ChatAttachmentItem = {
  localId: string;
  filename: string;
  status: AttachmentStatus;
};

let attachmentIdCounter = 0;
function nextLocalId(): string {
  return `attachment-${++attachmentIdCounter}`;
}

/**
 * Owns the attach-chip list for a chat bound to `experimentId`. Files are uploaded immediately
 * on pick via the agent-session documents REST endpoint, which attaches them to the session; the
 * agent then receives the session's full document set server-side, so nothing is forwarded on the
 * WS prompt. `reset()` aborts in-flight uploads and clears the chips.
 */
export function useChatAttachments(experimentId: ExperimentId) {
  const [items, setItems] = useState<ChatAttachmentItem[]>([]);
  const uploadMutation = useUploadSessionDocument();
  // In-flight upload aborts, so `reset()` can cancel them. Without this, an upload that lands
  // after a conversation clear would re-materialize the just-deleted session server-side.
  const pendingUploads = useRef(new Set<AbortController>());

  // Documents stay on the session after a send clears the compose chips (and across reloads), so
  // the slot count must include them, not just the chips. 403/5xx render as zero documents here;
  // the upload endpoint stays authoritative.
  const documentsQuery = useQuery({
    ...agentSessionDocumentsOptions(experimentId),
    enabled: Boolean(experimentId),
    throwOnError: false
  });

  const liveItems = useMemo(() => items.filter(i => i.status !== "error"), [items]);
  // Union by filename: names are unique per session, and a freshly uploaded chip overlaps with
  // its own row once the document list refetches, so it must not count twice.
  const uploadedCount = useMemo(() => {
    const sessionFilenames = documentsQuery.data?.documents.map(d => d.filename) ?? [];
    return new Set([...sessionFilenames, ...liveItems.map(i => i.filename)]).size;
  }, [documentsQuery.data, liveItems]);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) {
        return;
      }

      const admission = admitFiles(incoming, { existingCount: uploadedCount });
      notifyAdmissionWarnings(admission);

      for (const file of admission.accepted) {
        const localId = nextLocalId();
        setItems(prev => [...prev, { localId, filename: file.name, status: "uploading" }]);

        const controller = new AbortController();
        pendingUploads.current.add(controller);

        const formData = new FormData();
        formData.append("file", file);
        uploadMutation.mutate(
          { params: { path: { experiment_id: experimentId } }, body: formData, signal: controller.signal },
          {
            onSuccess: document => {
              // Take the server's filename: when concurrent identical-byte uploads race, the
              // loser gets the winner's row back, and keeping the local name would make the
              // slot-count union see two documents where the session has one.
              setItems(prev =>
                prev.map(i => (i.localId === localId ? { ...i, status: "uploaded", filename: document.filename } : i))
              );
            },
            onError: error => {
              // A reset()-driven abort isn't a failure the user needs to see — the chip is gone already.
              if (controller.signal.aborted) {
                return;
              }
              // The backend is authoritative on rejections (duplicate name or content, cumulative
              // cap) and returns a self-explanatory message that already names the file — show it
              // verbatim rather than burying it under a redundant "Failed to attach <name>" title.
              // Fall back to an explicit hint only when the API gives no detail.
              notifications.error(
                "Couldn't attach document",
                error.message ||
                  `${file.name} couldn't be uploaded — you may have reached the ${MAX_DOCS_PER_SESSION}-document limit for this chat.`
              );
              setItems(prev => prev.filter(i => i.localId !== localId));
            },
            onSettled: () => {
              pendingUploads.current.delete(controller);
            }
          }
        );
      }
    },
    [experimentId, uploadedCount, uploadMutation]
  );

  // Aborts in-flight uploads and drops their chips, keeping already-uploaded ones. Split from
  // `reset` so clear-conversation can stop late POSTs from re-creating the session BEFORE its
  // DELETE, while clearing the uploaded chips only once the DELETE succeeds — on failure the
  // documents still exist, so their chips should too.
  const abortPendingUploads = useCallback(() => {
    for (const controller of pendingUploads.current) {
      controller.abort();
    }
    pendingUploads.current.clear();
    setItems(prev => prev.filter(i => i.status !== "uploading"));
  }, []);

  const reset = useCallback(() => {
    abortPendingUploads();
    setItems([]);
  }, [abortPendingUploads]);

  const hasPendingUploads = useMemo(() => items.some(i => i.status === "uploading"), [items]);
  const remainingSlots = Math.max(0, MAX_DOCS_PER_SESSION - uploadedCount);

  return {
    items,
    addFiles,
    abortPendingUploads,
    reset,
    hasPendingUploads,
    remainingSlots
  };
}
